import React, { useState, useRef, useEffect } from 'react';
import { useScreenSize, useTouchCapability } from '../reports/MobileReportInteractions';

// Types for form input components
interface TouchFriendlyInputProps {
  name: string;
  id?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date';
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  showLabel?: boolean;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  min?: string | number;
  max?: string | number;
  pattern?: string;
}

interface TouchFriendlySelectProps {
  name: string;
  id?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[] | string[];
  label?: string;
  showLabel?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  placeholder?: string;
  grouped?: boolean;
  groups?: {
    label: string;
    options: { value: string; label: string }[] | string[];
  }[];
}

interface TouchFriendlyTextareaProps {
  name: string;
  id?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  label?: string;
  showLabel?: boolean;
  rows?: number;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

/**
 * Touch-friendly form input with improved spacing and touch targets
 * Features:
 * - Optimized for touch devices with larger touch targets
 * - Responsive spacing that adjusts based on orientation
 * - Visual feedback for touch interactions
 * - Error state handling
 * - Accessible labels
 */
export const TouchFriendlyInput: React.FC<TouchFriendlyInputProps> = ({
  name,
  id = name,
  type = 'text',
  value,
  onChange,
  placeholder,
  label,
  showLabel = false,
  autoComplete,
  required = false,
  disabled = false,
  error,
  className = '',
  min,
  max,
  pattern,
}) => {
  const isTouchDevice = useTouchCapability();
  const { isPortrait } = useScreenSize();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Touch-optimized padding and sizing
  const touchClasses = isTouchDevice
    ? isPortrait
      ? 'px-4 py-3 min-h-[48px]' // Portrait mode: taller
      : 'px-5 py-2.5 min-h-[48px]' // Landscape mode: wider
    : 'px-3 py-2';

  // Determine border color based on state
  const borderClasses = error
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : isFocused
    ? 'border-blue-500 focus:border-blue-500 focus:ring-blue-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  // Background based on disabled state
  const bgClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed'
    : 'bg-white';

  // Text sizing for better readability on mobile
  const textClasses = isTouchDevice
    ? 'text-base'
    : 'text-sm';

  return (
    <div className="w-full">
      {(label && showLabel) && (
        <label 
          htmlFor={id}
          className={`block mb-1 font-medium ${error ? 'text-red-600' : 'text-gray-700'} ${textClasses}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          pattern={pattern}
          aria-label={!showLabel && label ? label : undefined}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`
            w-full rounded-md border
            ${touchClasses}
            ${borderClasses}
            ${bgClasses}
            ${textClasses}
            shadow-sm appearance-none
            focus:outline-none focus:ring-2
            transition-colors duration-200
            ${className}
          `}
        />
      </div>
      
      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Touch-friendly select component with improved spacing and touch targets
 * Features:
 * - Optimized for touch devices with larger touch targets
 * - Responsive spacing that adjusts based on orientation
 * - Visual feedback for touch interactions
 * - Error state handling
 * - Supports grouped options
 */
export const TouchFriendlySelect: React.FC<TouchFriendlySelectProps> = ({
  name,
  id = name,
  value,
  onChange,
  options,
  groups,
  grouped = false,
  label,
  showLabel = false,
  placeholder,
  required = false,
  disabled = false,
  error,
  className = '',
}) => {
  const isTouchDevice = useTouchCapability();
  const { isPortrait } = useScreenSize();
  const selectRef = useRef<HTMLSelectElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Touch-optimized padding and sizing
  const touchClasses = isTouchDevice
    ? isPortrait
      ? 'px-4 py-3 min-h-[48px]' // Portrait mode: taller
      : 'px-5 py-2.5 min-h-[48px]' // Landscape mode: wider
    : 'px-3 py-2';

  // Determine border color based on state
  const borderClasses = error
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : isFocused
    ? 'border-blue-500 focus:border-blue-500 focus:ring-blue-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  // Background based on disabled state
  const bgClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed'
    : 'bg-white';

  // Text sizing for better readability on mobile
  const textClasses = isTouchDevice
    ? 'text-base'
    : 'text-sm';

  // Process options that can be either string[] or {value, label}[]
  const normalizeOptions = (opts: { value: string; label: string }[] | string[]) => {
    if (typeof opts[0] === 'string') {
      return (opts as string[]).map(opt => ({ value: opt, label: opt }));
    }
    return opts as { value: string; label: string }[];
  };

  return (
    <div className="w-full">
      {(label && showLabel) && (
        <label 
          htmlFor={id}
          className={`block mb-1 font-medium ${error ? 'text-red-600' : 'text-gray-700'} ${textClasses}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <select
          ref={selectRef}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          aria-label={!showLabel && label ? label : undefined}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`
            w-full rounded-md border
            ${touchClasses}
            ${borderClasses}
            ${bgClasses}
            ${textClasses}
            shadow-sm appearance-none
            focus:outline-none focus:ring-2
            transition-colors duration-200
            pr-10 
            ${className}
          `}
        >
          {placeholder && (
            <option value="" disabled={required}>
              {placeholder}
            </option>
          )}
          
          {grouped && groups ? (
            groups.map((group, groupIndex) => (
              <optgroup key={`group-${groupIndex}`} label={group.label}>
                {normalizeOptions(group.options).map((option, optionIndex) => (
                  <option key={`option-${groupIndex}-${optionIndex}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))
          ) : (
            normalizeOptions(options).map((option, index) => (
              <option key={`option-${index}`} value={option.value}>
                {option.label}
              </option>
            ))
          )}
        </select>
        
        {/* Custom dropdown arrow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
      
      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Touch-friendly textarea with improved spacing and touch targets
 * Features:
 * - Optimized for touch devices with larger touch targets
 * - Responsive spacing that adjusts based on orientation
 * - Visual feedback for touch interactions
 * - Error state handling
 */
export const TouchFriendlyTextarea: React.FC<TouchFriendlyTextareaProps> = ({
  name,
  id = name,
  value,
  onChange,
  placeholder,
  label,
  showLabel = false,
  rows = 4,
  required = false,
  disabled = false,
  error,
  className = '',
}) => {
  const isTouchDevice = useTouchCapability();
  const { isPortrait } = useScreenSize();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Touch-optimized padding and sizing
  const touchClasses = isTouchDevice
    ? isPortrait
      ? 'px-4 py-3' // Portrait mode: taller
      : 'px-5 py-2.5' // Landscape mode: wider
    : 'px-3 py-2';

  // Determine border color based on state
  const borderClasses = error
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : isFocused
    ? 'border-blue-500 focus:border-blue-500 focus:ring-blue-500'
    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';

  // Background based on disabled state
  const bgClasses = disabled
    ? 'bg-gray-100 cursor-not-allowed'
    : 'bg-white';

  // Text sizing for better readability on mobile
  const textClasses = isTouchDevice
    ? 'text-base'
    : 'text-sm';

  return (
    <div className="w-full">
      {(label && showLabel) && (
        <label 
          htmlFor={id}
          className={`block mb-1 font-medium ${error ? 'text-red-600' : 'text-gray-700'} ${textClasses}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <textarea
        ref={textareaRef}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        required={required}
        disabled={disabled}
        aria-label={!showLabel && label ? label : undefined}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`
          w-full rounded-md border
          ${touchClasses}
          ${borderClasses}
          ${bgClasses}
          ${textClasses}
          shadow-sm appearance-none
          focus:outline-none focus:ring-2
          transition-colors duration-200
          ${className}
        `}
      />
      
      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Touch-friendly form layout container with optimized spacing
 */
export const TouchFriendlyForm: React.FC<{
  children: React.ReactNode;
  className?: string;
  onSubmit?: (e: React.FormEvent) => void;
}> = ({ children, className = '', onSubmit }) => {
  const isTouchDevice = useTouchCapability();
  const { isPortrait } = useScreenSize();
  
  // Optimized spacing for touch devices based on orientation
  const spacing = isTouchDevice
    ? isPortrait
      ? 'space-y-6' // More vertical space in portrait
      : 'space-y-4' // Less vertical space in landscape
    : 'space-y-4';

  return (
    <form 
      onSubmit={onSubmit}
      className={`w-full ${spacing} ${className}`}
      noValidate
    >
      {children}
    </form>
  );
};

export default {
  TouchFriendlyInput,
  TouchFriendlySelect,
  TouchFriendlyTextarea,
  TouchFriendlyForm
};